import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { BaseViewComponent, Enum, FormBase, StepProgressKind } from '@sb/library/base';
import { TabGroupBodyComponent } from '../body/tab-group-body.component';
import { Observable, of, take } from 'rxjs';

@Component({
    selector: 'lib-step-progress-header',
    templateUrl: './step-progress-header.component.html',
    styleUrls: ['./step-progress-header.component.scss']
})
export class StepProgressHeaderComponent<T extends Enum<string>> extends BaseViewComponent implements AfterViewInit {

    @Input() bodyComponent!: TabGroupBodyComponent<T>;
    @Input() initialTab!: T;
    @Input() showOverflowArrows: boolean = true;
    @Output() stepChanged: EventEmitter<T> = new EventEmitter();

    @ViewChild('labelsContainer') labelsContainer!: ElementRef;
    @ViewChildren('labelItem') labelItems!: QueryList<ElementRef>;

    private _tabEnum: string[] = [];
    private _selectedIndex: number | null = null;
    private _isOverflowing: boolean = false;
    private _disableRightButton: boolean = false;
    private _disableLeftButton: boolean = false;

    private resizeObserver!: ResizeObserver;

    ngAfterViewInit() {

        setTimeout(() => { // need to wait for the items in labelsContainer to populate from the contentChildren
            this.ObserveParentSizeChanges();
        });

        this._tabEnum = this.bodyComponent.tabComponentQueryList.map(tabs => tabs.name) as string[];

        if (this.initialTab !== undefined) {
            this.SetSelectedTab(this.initialTab);
        } else {
            this.SelectedIndexChangeEvent(0);
        }

        this.bodyComponent.selectedIndex = this._selectedIndex;

    }

    ObserveParentSizeChanges() {
        const container = this.labelsContainer.nativeElement;

        this.resizeObserver = new ResizeObserver(() => {
            this.CheckOverflow();
        });

        this.resizeObserver.observe(container);
        this.labelItems.forEach(item => this.resizeObserver.observe(item.nativeElement));
        this.CheckOverflow();
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    CheckOverflow() {
        const container = this.labelsContainer.nativeElement;
        this._isOverflowing = container.scrollWidth > container.clientWidth;
        this.CenterItem(this._selectedIndex!);
        this.CalculateSideButtonsState();
    }

    GetStepProgressKind(index: number): StepProgressKind {
        if (this._selectedIndex == index) {
            return StepProgressKind.Active;
        }
        if (this._selectedIndex! > index) {
            return StepProgressKind.Completed;
        }
        return StepProgressKind.Inactive;
    }

    ArrowClicked(isLeft: boolean): void {
        this.labelsContainer.nativeElement.scrollBy({ left: isLeft ? -300 : 300, behavior: 'smooth' });
    }

    ScrollChanged(): void {
        this.CalculateSideButtonsState();
    }

    CenterItem(currentIndex: number): void {
        const labels = this.labelItems.toArray();
        const container = this.labelsContainer.nativeElement;

        if (!labels[currentIndex] || !container) {
            return;
        }

        const item = labels[currentIndex].nativeElement;
        const itemBounds = item.getBoundingClientRect();
        const containerBounds = container.getBoundingClientRect();
        container.scrollLeft += itemBounds.left - containerBounds.left - (container.clientWidth / 2 - item.clientWidth / 2);
        this.CalculateSideButtonsState();
    }

    // Next, Previous and GoToStep subscriptions fire when the tab animation completes
    Next(): Observable<void> {
        if (this._selectedIndex! < this.labelItems.length) {
            return this.SelectedIndexChangeEvent(this._selectedIndex! += 1);
        }
        return of(undefined);
    }

    Previous(): Observable<void> {
        if (this._selectedIndex! > 0) {
            return this.SelectedIndexChangeEvent(this._selectedIndex! -= 1);
        }
        return of(undefined);
    }

    GoToStep(tab: T): Observable<void> {
        const index = this._tabEnum.indexOf(tab as string);
        if (index >= 0) {
            return this.SelectedIndexChangeEvent(index);
        } else {
            console.error('Selected tab passed in as input does not exist');
            return of(undefined);
        }
    }

    private CalculateSideButtonsState(): void {
        const container = this.labelsContainer.nativeElement;
        this._disableRightButton = container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;
        this._disableLeftButton = container.scrollLeft <= 0;
    }

    private SetSelectedTab(value: T) {
        const index = this._tabEnum.indexOf(value);
        if (index >= 0) {
            this.SelectedIndexChangeEvent(index);
        } else {
            console.error('Selected tab passed in as input does not exist');
        }
    }

    private SelectedIndexChangeEvent(index: number, methodPipeCacheBuster?: any): Observable<void> {
        this._selectedIndex = index;
        this.bodyComponent.selectedIndex = index;
        this.CenterItem(this._selectedIndex!); // is mobile only
        this.stepChanged.emit(this._tabEnum[this._selectedIndex] as T);

        return this.bodyComponent.animationDone.pipe(take(1));
    }

    get isOverflowing(): boolean {
        return this._isOverflowing;
    }

    get StepProgressKind(): typeof StepProgressKind {
        return StepProgressKind;
    }

    get selectedIndex(): number | null {
        return this._selectedIndex;
    }

    get currentStep(): T {
        return this._tabEnum[this._selectedIndex!] as T;
    }

    get isLastStep(): boolean {
        return this.labelItems && this._selectedIndex == (this.labelItems.length - 1);
    }

    get isFirstStep(): boolean {
        return this._selectedIndex == 0;
    }

    get disableRightButton(): boolean {
        return this._disableRightButton;
    }

    get disableLeftButton(): boolean {
        return this._disableLeftButton;
    }

    get canScrollLeft(): boolean {
        return this._isOverflowing && !this._disableLeftButton;
    }

    get canScrollRight(): boolean {
        return this._isOverflowing && !this._disableRightButton;
    }
}
